import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { 
  AcademicCapIcon,
  ClockIcon,
  CheckCircleIcon,
  ChartBarIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { MOCK_DATA } from '../../lib/mock/viag/learn/data/mockData';

// Add this utility function to generate color backgrounds based on course ID
const getBackgroundColor = (id) => {
  // Map of course IDs to background color classes
  const colorMap = {
    'AGS001': 'bg-blue-500',
    'AGS002': 'bg-red-500',
    'AGS003': 'bg-indigo-500',
    'AGS004': 'bg-orange-500',
    'AGS005': 'bg-yellow-500',
    'AGS006': 'bg-purple-500',
    'AGS007': 'bg-green-500',
    'AGS008': 'bg-gray-500',
    'AGS009': 'bg-teal-500',
    'AGS010': 'bg-pink-500',
  };
  
  return colorMap[id] || 'bg-gray-400';
};

const LearnDashboard = () => {
  const { t, locale } = useLanguage();
  const [learningData, setLearningData] = useState(null);
  const [courses, setCourses] = useState(null);
  const [loading, setLoading] = useState(true);

  // Simulate API call to get data
  useEffect(() => {
    const fetchData = async () => {
      // In a real application, this would be an API call
      setTimeout(() => {
        setLearningData({
          inProgress: MOCK_DATA.learningProgress.inProgress,
          completed: MOCK_DATA.learningProgress.completed,
          totalAssigned: MOCK_DATA.learningProgress.totalAssigned,
          completionRate: MOCK_DATA.learningProgress.completionRate,
          certificatesEarned: MOCK_DATA.learningProgress.certificatesEarned,
          activeClasses: MOCK_DATA.learningProgress.activeClasses,
          upcomingClasses: MOCK_DATA.learningProgress.upcomingClasses,
          completedClasses: MOCK_DATA.learningProgress.completedClasses,
          recentActivity: MOCK_DATA.recentActivity,
          upcomingDeadlines: MOCK_DATA.upcomingDeadlines
        });
        
        setCourses({
          recommended: MOCK_DATA.getRecommendedCourses(),
          assigned: MOCK_DATA.courses.filter(course => 
            course.status === 'in_progress' || 
            course.status === 'not_started'
          )
        });
        
        setLoading(false);
      }, 800);
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  // Helpers for translated activity content
  const getActivityContent = (activity) => {
    const activityParams = { course: activity.course, progress: activity.progress };
    
    switch(activity.type) {
      case 'course_progress':
        return t('learn.dashboard.activity.progressUpdate', { course: activity.course, progress: activity.progress });
      case 'course_completed':
        return t('learn.dashboard.activity.courseCompleted', { course: activity.course });
      case 'certificate_earned':
        return t('learn.dashboard.activity.certificateEarned', { course: activity.course });
      case 'course_started':
        return t('learn.dashboard.activity.courseStarted', { course: activity.course });
      case 'quiz_completed':
        return locale === 'vi'
          ? `Hoàn thành bài kiểm tra "${activity.quizName}" với điểm số ${activity.score}/100`
          : `Completed quiz "${activity.quizName}" with score ${activity.score}/100`;
      case 'content_viewed':
        return locale === 'vi'
          ? `Đã xem bài học "${activity.contentName}"`
          : `Viewed lesson "${activity.contentName}"`;
      case 'class_enrolled':
        return locale === 'vi'
          ? `Đã tham gia lớp học "${activity.className}"`
          : `Enrolled in class "${activity.className}"`;
      default:
        return '';
    }
  };

  return (
    <div className="space-y-8">
      {/* Learning Progress Overview */}
      <div>
        <h2 className="text-xl font-semibold text-gray-800 mb-4">{t('learn.dashboard.progressOverview')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center justify-between mb-3">
              <div className="text-gray-500 text-sm font-medium">{t('learn.dashboard.inProgressCourses')}</div>
              <div className="bg-blue-100 p-2 rounded-lg">
                <ClockIcon className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            <div className="text-2xl font-bold">{learningData.inProgress}</div>
            <div className="text-sm text-gray-500 mt-1">{t('learn.dashboard.coursesInProgress')}</div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center justify-between mb-3">
              <div className="text-gray-500 text-sm font-medium">{t('learn.dashboard.completedCourses')}</div>
              <div className="bg-green-100 p-2 rounded-lg">
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <div className="text-2xl font-bold">{learningData.completed}</div>
            <div className="text-sm text-gray-500 mt-1">{t('learn.dashboard.coursesCompleted')}</div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center justify-between mb-3">
              <div className="text-gray-500 text-sm font-medium">{t('learn.dashboard.certificates')}</div>
              <div className="bg-yellow-100 p-2 rounded-lg">
                <AcademicCapIcon className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
            <div className="text-2xl font-bold">{learningData.certificatesEarned}</div>
            <div className="text-sm text-gray-500 mt-1">{t('learn.dashboard.certificatesEarned')}</div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center justify-between mb-3">
              <div className="text-gray-500 text-sm font-medium">{t('learn.dashboard.classes')}</div>
              <div className="bg-indigo-100 p-2 rounded-lg">
                <UserGroupIcon className="h-5 w-5 text-indigo-600" />
              </div>
            </div>
            <div className="text-2xl font-bold">{learningData.activeClasses + learningData.upcomingClasses}</div>
            <div className="text-sm text-gray-500 mt-1">
              {learningData.activeClasses} {t('learn.dashboard.active')}, {learningData.upcomingClasses} {t('learn.dashboard.upcoming')}
            </div>
          </div>
        </div>
      </div>

      {/* Assigned Courses */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">{t('learn.dashboard.assignedCourses')}</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {courses.assigned.slice(0, 4).map(course => (
            <div key={course.id} className="bg-white rounded-lg shadow overflow-hidden flex flex-col border border-gray-100">
              <div className={`h-36 relative flex items-center justify-center ${getBackgroundColor(course.id)} text-white`}>
                <span className="text-lg font-bold">{locale === 'vi' ? course.title.split(' ').slice(0, 2).join(' ') : course.titleEn.split(' ').slice(0, 2).join(' ')}</span>
                {course.isRequired && (
                  <div className="absolute top-2 right-2 bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded">
                    {t('learn.courseInfo.required')}
                  </div>
                )}
              </div>
              <div className="p-4 flex-grow">
                <h3 className="font-medium text-gray-900 mb-1 line-clamp-2">
                  {locale === 'vi' ? course.title : course.titleEn}
                </h3>
                <p className="text-gray-500 text-sm mb-3">
                  {course.instructor} • {course.duration}
                </p>
                <div className="mb-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full"
                      style={{ width: `${course.progress}%` }}
                    ></div>
                  </div>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">{course.progress}% {t('learn.dashboard.complete')}</span>
                  {course.dueDate && (
                    <span className="text-gray-600">
                      <span className="inline-flex items-center">
                        <CalendarDaysIcon className="h-3 w-3 mr-1" />
                        {course.dueDate}
                      </span>
                    </span>
                  )}
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 border-t text-center">
                <Link 
                  to={`/learn/course/${course.id}`}
                  className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
                >
                  {course.progress > 0 ? t('learn.courseActions.continueLearning') : t('learn.courseActions.startLearning')}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity and Upcoming Deadlines */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">{t('learn.dashboard.recentActivity')}</h2>
          <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-100">
            <ul className="divide-y divide-gray-200">
              {learningData.recentActivity.map(activity => (
                <li key={activity.id} className="p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      {activity.type === 'course_progress' && (
                        <div className="bg-blue-100 p-1.5 rounded-full">
                          <ClockIcon className="h-4 w-4 text-blue-600" />
                        </div>
                      )}
                      {activity.type === 'course_completed' && (
                        <div className="bg-green-100 p-1.5 rounded-full">
                          <CheckCircleIcon className="h-4 w-4 text-green-600" />
                        </div>
                      )}
                      {activity.type === 'certificate_earned' && (
                        <div className="bg-yellow-100 p-1.5 rounded-full">
                          <AcademicCapIcon className="h-4 w-4 text-yellow-600" />
                        </div>
                      )}
                      {activity.type === 'course_started' && (
                        <div className="bg-indigo-100 p-1.5 rounded-full">
                          <BookOpenIcon className="h-4 w-4 text-indigo-600" />
                        </div>
                      )}
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-sm text-gray-700">
                        {getActivityContent(activity)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {activity.timestamp}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Upcoming Deadlines */}
        <div>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">{t('learn.dashboard.upcomingDeadlines')}</h2>
          <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-100 h-full">
            {learningData.upcomingDeadlines.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {learningData.upcomingDeadlines.map(deadline => (
                  <li key={deadline.id} className="p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <CalendarDaysIcon className="h-5 w-5 text-red-500" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">
                          {locale === 'vi' ? deadline.title : deadline.titleEn}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {t('learn.dashboard.due')}: {deadline.dueDate}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-4 text-center">
                <p className="text-sm text-gray-500">{t('learn.dashboard.noUpcomingDeadlines')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearnDashboard;